import React, { useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { SettingsProvider } from './context/SettingsContext';
import { Toaster } from './components/ui/toaster';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import MobileModuleTabs from './components/layout/MobileModuleTabs';
import MobileBottomNav from './components/layout/MobileBottomNav';
import Home from './pages/Home';
import LiveTrainStatus from './pages/LiveTrainStatus';
import PNRStatus from './pages/PNRStatus';
import FoodOnTrain from './pages/FoodOnTrain';
import StationRestaurants from './pages/StationRestaurants';
import RestaurantMenu from './pages/RestaurantMenu';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import OrderSuccess from './pages/OrderSuccess';
import Login from './pages/Login';
import MyOrders from './pages/MyOrders';
import Offers from './pages/Offers';
import TrainTools from './pages/TrainTools';
import AdminLogin from './pages/admin/AdminLogin';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminTrains from './pages/admin/AdminTrains';
import AdminRestaurants from './pages/admin/AdminRestaurants';
import AdminOrders from './pages/admin/AdminOrders';
import AdminUsers from './pages/admin/AdminUsers';
import AdminScrapeLogs from './pages/admin/AdminScrapeLogs';
import AdminSettings from './pages/admin/AdminSettings';
import AdminStories from './pages/admin/AdminStories';
import AdminCabBookings from './pages/admin/AdminCabBookings';
import AdminBanners from './pages/admin/AdminBanners';
import AdminPages from './pages/admin/AdminPages';
import Stories from './pages/Stories';
import StoryDetail from './pages/StoryDetail';
import CabBooking from './pages/CabBooking';
import Page from './pages/Page';
import ModuleGuard from './components/ModuleGuard';
import GoogleAnalytics from './components/GoogleAnalytics';
import FeatureNote from './components/FeatureNote';
import FamilyPage from './features/family/FamilyPage';
import { SosFloatingButton, SosTrackingPage } from './features/sos';
import AdminSosAlerts from './pages/admin/AdminSosAlerts';
import NotFound from './pages/NotFound';

const PublicShell = ({ children }) => (
  <>
    <Navbar />
    <MobileModuleTabs />
    <div className="pb-12 md:pb-0">{children}<FeatureNote /></div>
    <div className="hidden md:block"><Footer /></div>
    <MobileBottomNav />
    <SosFloatingButton />
  </>
);

// Cabs route: rendered chrome-less when embedded inside the mobile app (?embed=1).
const CabRoute = () => {
  const embed = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('embed') === '1';
  return embed
    ? <><CabBooking /><FeatureNote featureKey="cabs" /></>
    : <PublicShell><CabBooking /></PublicShell>;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <SettingsProvider>
          <CartProvider>
          <BrowserRouter>
            <GoogleAnalytics />
            <Routes>
              <Route path="/" element={<PublicShell><Home /></PublicShell>} />
              <Route path="/live-train-status" element={<ModuleGuard name="live_status"><PublicShell><LiveTrainStatus /></PublicShell></ModuleGuard>} />
              <Route path="/pnr-status" element={<ModuleGuard name="pnr_status"><PublicShell><PNRStatus /></PublicShell></ModuleGuard>} />
              <Route path="/food-on-train" element={<ModuleGuard name="food_on_train"><PublicShell><FoodOnTrain /></PublicShell></ModuleGuard>} />
              <Route path="/food/station/:code" element={<ModuleGuard name="food_on_train"><PublicShell><StationRestaurants /></PublicShell></ModuleGuard>} />
              <Route path="/food/restaurant/:id" element={<ModuleGuard name="food_on_train"><PublicShell><RestaurantMenu /></PublicShell></ModuleGuard>} />
              <Route path="/cart" element={<PublicShell><Cart /></PublicShell>} />
              <Route path="/checkout" element={<PublicShell><Checkout /></PublicShell>} />
              <Route path="/order-success/:id" element={<PublicShell><OrderSuccess /></PublicShell>} />
              <Route path="/login" element={<PublicShell><Login /></PublicShell>} />
              <Route path="/my-orders" element={<PublicShell><MyOrders /></PublicShell>} />
              <Route path="/offers" element={<PublicShell><Offers /></PublicShell>} />
              <Route path="/trains" element={<PublicShell><TrainTools /></PublicShell>} />
              <Route path="/stories" element={<ModuleGuard name="stories"><PublicShell><Stories /></PublicShell></ModuleGuard>} />
              <Route path="/stories/:slug" element={<ModuleGuard name="stories"><PublicShell><StoryDetail /></PublicShell></ModuleGuard>} />
              <Route path="/cabs" element={<ModuleGuard name="cab_booking"><CabRoute /></ModuleGuard>} />
              <Route path="/family" element={<PublicShell><FamilyPage /></PublicShell>} />
              <Route path="/sos/track/:alertId" element={<SosTrackingPage />} />
              <Route path="/page/:slug" element={<PublicShell><Page /></PublicShell>} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="sos-alerts" element={<AdminSosAlerts />} />
                <Route path="trains" element={<AdminTrains />} />
                <Route path="restaurants" element={<AdminRestaurants />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="scrape-logs" element={<AdminScrapeLogs />} />
                <Route path="stories" element={<AdminStories />} />
                <Route path="cab-bookings" element={<AdminCabBookings />} />
                <Route path="banners" element={<AdminBanners />} />
                <Route path="pages" element={<AdminPages />} />
                <Route path="settings" element={<AdminSettings />} />
              </Route>
              <Route path="*" element={<PublicShell><NotFound /></PublicShell>} />
            </Routes>
          </BrowserRouter>
          <Toaster />
        </CartProvider>
        </SettingsProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
